import React, { useState } from 'react';
import { Card, Button } from '../UI';
import { 
  User, 
  Settings, 
  Bell, 
  Lock, 
  Mail, 
  LogOut, 
  HelpCircle, 
  Info,
  Leaf,
  ChevronRight,
  Edit2
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const ProfileView = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await signOut();
      navigate('/');
    }
  };

  if (showSettings) {
    return <SettingsSheet onClose={() => setShowSettings(false)} />;
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Profile Header */}
      <Card className="mb-lg p-xl">
        <div className="flex items-center gap-lg">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-accent/10 flex items-center justify-center">
            <User size={40} className="text-primary" />
          </div>
          <div className="flex-1">
            <h1 className="text-title-1 font-rounded text-primary mb-xs">
              {user?.displayName || 'User'}
            </h1>
            <p className="text-body text-text-secondary">{user?.email}</p>
          </div>
          <Button
            variant="outline"
            icon={<Edit2 size={18} />}
            onClick={() => navigate('/profile/edit')}
          >
            Edit
          </Button>
        </div>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-md mb-lg">
        <Card 
          className="p-lg cursor-pointer hover:shadow-md transition-all"
          onClick={() => navigate('/dashboard')}
        >
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-md">
              <User size={24} className="text-primary" />
            </div>
            <h3 className="text-callout font-semibold text-primary mb-xs">Dashboard</h3>
            <p className="text-caption text-text-secondary">View your overview</p>
          </div>
        </Card>

        <Card 
          className="p-lg cursor-pointer hover:shadow-md transition-all"
          onClick={() => navigate('/recipes/saved')}
        >
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-md">
              <Leaf size={24} className="text-accent" />
            </div>
            <h3 className="text-callout font-semibold text-primary mb-xs">Saved Recipes</h3>
            <p className="text-caption text-text-secondary">Your cookbook</p>
          </div>
        </Card>

        <Card 
          className="p-lg cursor-pointer hover:shadow-md transition-all"
          onClick={() => setShowSettings(true)}
        >
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-md">
              <Settings size={24} className="text-primary" />
            </div>
            <h3 className="text-callout font-semibold text-primary mb-xs">Settings</h3>
            <p className="text-caption text-text-secondary">Manage preferences</p>
          </div>
        </Card>
      </div>

      {/* Settings Preview */}
      <Card className="p-lg">
        <div className="flex items-center justify-between mb-lg">
          <h2 className="text-title-2 font-rounded text-primary">Quick Settings</h2>
          <Button
            variant="outline"
            size="small"
            onClick={() => setShowSettings(true)}
          >
            View All
          </Button>
        </div>

        <div className="space-y-xs">
          <SettingsRow
            icon={<Leaf size={20} />}
            title="Dietary Preferences"
            onClick={() => navigate('/profile/dietary')}
          />
          <SettingsRow
            icon={<Bell size={20} />}
            title="Notifications"
            onClick={() => navigate('/profile/notifications')}
          />
          <SettingsRow
            icon={<Lock size={20} />}
            title="Privacy"
            onClick={() => navigate('/profile/privacy')}
          />
        </div>
      </Card>

      {/* Logout */}
      <div className="mt-lg">
        <Button
          variant="outline"
          icon={<LogOut size={18} />}
          onClick={handleLogout}
          className="w-full text-error border-error/30 hover:bg-error/5"
        >
          Sign Out
        </Button>
      </div>
    </div>
  );
};

// Settings Sheet Component
const SettingsSheet = ({ onClose }) => {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await signOut();
      navigate('/');
      onClose();
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-lg">
        <h1 className="text-display font-rounded text-primary">Settings</h1>
        <Button variant="outline" size="small" onClick={onClose}>
          Close
        </Button>
      </div>

      <div className="space-y-lg">
        {/* Profile Section */}
        <Card className="overflow-hidden">
          <div className="p-lg border-b border-text-tertiary/10">
            <h2 className="text-title-3 text-primary">Profile</h2>
          </div>
          <SettingsRow
            icon={<Leaf size={20} />}
            title="Dietary Preferences & Allergies"
            subtitle="Manage dietary restrictions"
            onClick={() => navigate('/profile/dietary')}
          />
        </Card>

        {/* App Settings Section */}
        <Card className="overflow-hidden">
          <div className="p-lg border-b border-text-tertiary/10">
            <h2 className="text-title-3 text-primary">App Settings</h2>
          </div>
          <SettingsRow
            icon={<Bell size={20} />}
            title="Notifications"
            onClick={() => navigate('/profile/notifications')}
          />
          <div className="border-t border-text-tertiary/10 ml-14" />
          <SettingsRow
            icon={<Lock size={20} />}
            title="Privacy"
            onClick={() => navigate('/profile/privacy')}
          />
        </Card>

        {/* Account Section */}
        <Card className="overflow-hidden">
          <div className="p-lg border-b border-text-tertiary/10">
            <h2 className="text-title-3 text-primary">Account</h2>
          </div>
          <SettingsRow
            icon={<Mail size={20} />}
            title="Email & Password"
            onClick={() => navigate('/profile/account')}
          />
          <div className="border-t border-text-tertiary/10 ml-14" />
          <SettingsRow
            icon={<LogOut size={20} />}
            title="Logout & Delete Account"
            onClick={handleLogout}
          />
        </Card>

        {/* Support Section */}
        <Card className="overflow-hidden">
          <SettingsRow
            icon={<HelpCircle size={20} />}
            title="Help & Support"
            onClick={() => window.open('https://nutrius.co/support', '_blank')}
          />
          <div className="border-t border-text-tertiary/10 ml-14" />
          <SettingsRow
            icon={<Info size={20} />}
            title="About Nutrius"
            onClick={() => navigate('/about')}
          />
        </Card>

        {/* App Version */}
        <p className="text-center text-caption text-text-secondary py-md">
          Nutrius v1.0.0
        </p>
      </div>
    </div>
  );
};

// Reusable Settings Row Component
const SettingsRow = ({ icon, title, subtitle, value, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-md p-md hover:bg-primary/5 transition-colors text-left"
    >
      <div className="text-primary flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-body text-primary font-medium">{title}</p>
        {subtitle && (
          <p className="text-caption text-text-secondary">{subtitle}</p>
        )}
      </div>
      {value && (
        <p className="text-callout text-text-secondary">{value}</p>
      )}
      <ChevronRight size={16} className="text-text-secondary flex-shrink-0" />
    </button>
  );
};

export default ProfileView;